import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, RotateCcw, Timer, Volume2, VolumeX, CheckCircle2, Circle, AlertTriangle, Droplets } from "lucide-react";

const CHECKLIST_ITEMS = [
  { id: "consent", label: "Consent obtained for transfusion" },
  { id: "type", label: "Blood type verified (type-specific or O neg)" },
  { id: "access", label: "Adequate venous access established (2 large bore IVs / CVC)" },
  { id: "tubing", label: "Standard blood tubing with 170-micron filter selected" },
  { id: "warmer", label: "Fluid warmer connected (maintain temp > 34°C)" },
  { id: "txa", label: "Trauma: Consider TXA 1 g IV" },
  { id: "baseline", label: "Baseline vitals recorded (HR, BP, SpO2, Temp, RR)" },
  { id: "start", label: "Transfusion started — slow rate for first 15 min" },
];

const VS_INTERVAL = 15 * 60; // 15 minutes in seconds
const MAX_DURATION = 4 * 60 * 60; // 4 hours in seconds

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function playAlert(ctx) {
  if (!ctx) return;
  // Three-tone alert
  [0, 200, 400].forEach((delay) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.value = 0.4;
    const t = ctx.currentTime + delay / 1000;
    o.start(t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    o.stop(t + 0.15);
  });
}

export default function BloodTransfusionTimer() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [checked, setChecked] = useState({});
  const [vsLog, setVsLog] = useState([]);
  const [alertActive, setAlertActive] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const lastVsRef = useRef(0);

  const inSlowPhase = elapsed > 0 && elapsed <= 15 * 60;
  const timeToNextVs = VS_INTERVAL - (elapsed - lastVsRef.current);
  const overdue = elapsed >= MAX_DURATION;
  const checkedCount = Object.values(checked).filter(Boolean).length;

  const ensureAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  }, []);

  const start = useCallback(() => {
    ensureAudio();
    setRunning(true);
  }, [ensureAudio]);

  const pause = useCallback(() => setRunning(false), []);

  const reset = useCallback(() => {
    setRunning(false);
    setElapsed(0);
    setVsLog([]);
    setAlertActive(false);
    lastVsRef.current = 0;
  }, []);

  const acknowledgeVs = useCallback(() => {
    const timestamp = formatTime(elapsed);
    setVsLog(prev => [...prev, { time: timestamp, elapsed }]);
    lastVsRef.current = elapsed;
    setAlertActive(false);
  }, [elapsed]);

  const toggleCheck = (id) => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Timer tick
  useEffect(() => {
    if (!running) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  // VS alert check
  useEffect(() => {
    if (!running) return;
    const sinceLastVs = elapsed - lastVsRef.current;
    if (sinceLastVs >= VS_INTERVAL && !alertActive) {
      setAlertActive(true);
      if (soundEnabled) playAlert(audioCtxRef.current);
    }
  }, [elapsed, running, alertActive, soundEnabled]);

  const progressPct = Math.min((elapsed / MAX_DURATION) * 100, 100);

  return (
    <Card className={`border-red-500/30 ${alertActive ? "ring-2 ring-red-500 animate-pulse" : ""}`} data-testid="transfusion-timer">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-red-400" />
            Blood Transfusion Timer
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSoundEnabled(!soundEnabled)} data-testid="transfusion-sound">
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset} data-testid="transfusion-reset">
              <RotateCcw className="w-3 h-3 mr-1" />Reset
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-4">

        {/* Timer Display */}
        <div className={`rounded-lg border p-4 text-center ${alertActive ? "bg-red-500/15 border-red-500/50" : overdue ? "bg-amber-500/15 border-amber-500/50" : "bg-muted/30 border-border"}`} data-testid="transfusion-timer-display">
          <div className="text-4xl font-bold font-mono">
            <span className={alertActive ? "text-red-400" : overdue ? "text-amber-400" : "text-foreground"}>{formatTime(elapsed)}</span>
          </div>

          {inSlowPhase && (
            <div className="mt-1 text-xs font-medium text-amber-400">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              SLOW RATE — First 15 minutes, monitor for reaction
            </div>
          )}

          {alertActive && (
            <div className="mt-2">
              <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={acknowledgeVs} data-testid="transfusion-ack-vs">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Vital Signs Done — Acknowledge
              </Button>
            </div>
          )}

          {!alertActive && elapsed > 0 && (
            <div className="mt-1 text-xs text-muted-foreground">
              Next vitals in <span className="font-mono font-bold">{formatTime(Math.max(0, timeToNextVs))}</span>
            </div>
          )}

          {overdue && (
            <div className="mt-1 text-xs font-medium text-amber-400">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              4-hour window exceeded — complete transfusion
            </div>
          )}

          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-muted/40 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${overdue ? "bg-amber-500" : "bg-red-400"}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
            <span>0 min</span>
            <span>1 hr</span>
            <span>2 hr</span>
            <span>3 hr</span>
            <span>4 hr</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex gap-2 justify-center">
          {!running ? (
            <Button size="sm" onClick={start} className="gap-1.5" data-testid="transfusion-start">
              <Play className="w-4 h-4" />{elapsed === 0 ? "Start Transfusion" : "Resume"}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={pause} className="gap-1.5" data-testid="transfusion-pause">
              <Pause className="w-4 h-4" />Pause
            </Button>
          )}
        </div>

        {/* Pre-Transfusion Checklist */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-heading font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-red-400" />
              Pre-Transfusion Checklist
            </span>
            <Badge variant="outline" className="text-xs font-mono">{checkedCount}/{CHECKLIST_ITEMS.length}</Badge>
          </div>
          {CHECKLIST_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => toggleCheck(item.id)}
              className={`w-full flex items-center gap-2.5 p-2 rounded-md text-left text-sm transition-all ${
                checked[item.id]
                  ? "bg-green-500/10 border border-green-500/30 text-green-300 line-through opacity-80"
                  : "bg-secondary/20 border border-border hover:border-red-500/30"
              }`}
              data-testid={`transfusion-check-${item.id}`}
            >
              {checked[item.id] ? (
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              {item.label}
            </button>
          ))}
        </div>

        {/* Vital Signs Log */}
        {vsLog.length > 0 && (
          <div className="space-y-1">
            <span className="text-sm font-heading font-semibold flex items-center gap-1.5">
              <Timer className="w-4 h-4 text-blue-400" />
              Vital Signs Log
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {vsLog.map((entry, idx) => (
                <div key={idx} className="bg-blue-500/10 border border-blue-500/20 rounded px-2.5 py-1.5 text-center">
                  <div className="text-xs text-muted-foreground">VS Check #{idx + 1}</div>
                  <div className="text-sm font-mono font-bold text-blue-400">{entry.time}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
